import { InfoCircleOutlined } from '@ant-design/icons';
import {
  AutoComplete,
  Col,
  DatePicker,
  Form,
  Input,
  Row,
  Select,
  Space,
  Tabs,
  Typography,
  message,
} from 'antd';
import axios from 'axios';
import { debounce } from 'lodash';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { AiOutlineInfoCircle } from 'react-icons/ai';
import { CiLocationOn } from 'react-icons/ci';
import { IoDocumentTextOutline } from 'react-icons/io5';
import { MdDateRange } from 'react-icons/md';

import AddressUnitsSelector from '../../../components/common/AddressUnitSelector';
import { isHandledByInterceptor } from '../../../utils/apiError';
import {
  CUSTOMER_NAME_MAX_LENGTH,
  EMAIL_MAX_LENGTH,
  maxLengthRule,
  phoneNumberRules,
  requiredText,
} from '../../../utils/formRules';
import AddressMap from '../../../components/map/AddressMap';
import CabinetMap from '../../../components/map/CabinetMap';
import '../style.less';
import { getDeliveryTypesDependOnContract } from '../utils/utils';
import { AddOrderDeliveryContext } from './AddOrderDeliveryContext';
import OutOfZoneNotice from './OutOfZoneNotice';
import { formatAddressUnits, isValidAddressUnits, normalizeAddressUnits } from './addressUtils';
import PPBoxSelection from './components/PPBoxSelection';
import SectionCard from '../../../components/sectionCard/SectionCard';
import StepFooter from '../../../components/stepFooter/StepFooter';
import { getCustomField } from './helper';
import useOutOfZone from './useOutOfZone';
import { buildZoneQuery, calcOutOfZoneFee } from './zoneUtils';

const { Option } = Select;
const { TextArea } = Input;
const { Text } = Typography;

/** Талбарууд картын бүтэн өргөнөөр сунахгүй — зүүн талд багцлагдана */
const FORM_MAX_WIDTH = 960;

/** Хаяг сонгоогүй үеийн байрлал — Улаанбаатарын төв ([өргөрөг, уртраг]) */
const DEFAULT_POSITION = [47.9189, 106.9176];

/** Формын утга хадгалагдах хоцролт — гар бичихэд алхам бүрд бичихгүй */
const PERSIST_DELAY = 400;

/** Эрлианы захиалгад хаяг тогтмол — Улаанбаатар, ХУД, 1-р хороо */
const UB = [
  {
    key: '11',
    value: '11',
    children: 'Улаанбаатар',
    order: 100,
    type: 'aimaghot',
    addressId: '11',
    name: 'Улаанбаатар',
  },
  {
    key: '1101',
    value: '1101',
    children: 'Хан-Уул дүүрэг',
    order: 200,
    type: 'sumduureg',
    addressId: '1101',
    name: 'Хан-Уул дүүрэг',
  },
  {
    key: '110101',
    value: '110101',
    children: '1-р хороо',
    order: 300,
    type: 'baghoroo',
    addressId: '110101',
    name: '1-р хороо',
  },
];

/** Эрлианы хэрэглэгчийн тогтмол мэдээлэл */
const ERLIAN_CUSTOMER = {
  phoneNumber: '88059110',
  firstName: 'Enkhbat',
  email: 'enkhbat.d@ppworldwide.mn',
};

/** Хаягийн сонголтууд мөрөндөө ижил зайтай байхын тулд */
const addressStyleProps = { rowGutter: [16, 0], columnSpans: [8, 8, 8] };

const Step2 = ({ prevStep, nextStep }) => {
  const [form] = Form.useForm();
  const phoneValue = Form.useWatch('phoneNumber', form);
  const deliveryDetailValue = Form.useWatch('deliveryDetail', form);

  const {
    reference,
    formDataStep2,
    setFormDataStep2,
    formValuesStep2,
    setFormValuesStep2,
    customFields,
    addressArray,
    formDataStep1,
    setAddressArray,
    warehouseList,
    branchList,
    bagHorooId,
    selectedCustomerField,
    setSelectedCustomerField,
    selectedTab,
    setSelectedTab,
    selectedDeliveryType,
    setSelelctedDeliveryType,
    selectedDeliveryTime,
    setSelelctedDeliveryTime,
    selectedBox,
    setSelectedBox,
    setSelectedLocker,
    selectedLocker,
    selectedDeliveryAddress,
    setAddressDetail,
  } = useContext(AddOrderDeliveryContext);

  const [phoneOptions, setPhoneOptions] = useState([]);
  /** Хуучин хүсэлтийн хожуу ирсэн хариу шинэ хариуг дарж бичихээс сэргийлнэ */
  const phoneSearchSeq = useRef(0);
  /** Мэдээллийг нь формд буулгасан сүүлийн харилцагчийн дугаар */
  const prefilledPhoneRef = useRef(null);
  const [submitting, setSubmitting] = useState(false);

  const merchantCode = reference?.contract?.data?.merchantCode;
  const deliveryType = reference?.deliveryType;
  const deliveryTime = reference?.type;
  const isErlian = merchantCode === 'EHW';

  /**
   * Step1 дээр тодорхойлогдсон гэрээний төрөл ('W' | 'O' | 'WO' | 'D').
   * `reference`-ээс шууд авбал массив байх тул доорх шалгалтууд ажилладаггүй
   * байсан (['WO'].includes('W') === false).
   */
  const contractType = formDataStep1?.contractType ?? reference?.contract?.data?.contractType;

  const solvedPrice =
    formDataStep1?.solvedPrice ??
    (reference?.contract?.data?.services?.find((service) => service?.serviceType === 'delivery')
      ?.isB2B
      ? 'B2Bprice'
      : 'B2Cprice');

  const isCabinet = selectedDeliveryType === 'deliverytocabinet';
  const selfPickUp = selectedDeliveryType === 'deliveryselfproclaim';

  // ondemand гэрээтэй хэрэглэгч шуурхай хүргэлт үүсгэх боломжгүй
  const isDeliveryTimeOnlyRegular = reference?.isExpress
    ? false
    : contractType === 'O' ||
      (contractType === 'W' && solvedPrice === 'B2Bprice') ||
      (contractType === 'WO' && solvedPrice === 'B2Bprice');

  const isDeliveryTypeOnlyDelivery =
    (contractType === 'WO' && solvedPrice === 'B2Bprice') ||
    (contractType === 'O' && solvedPrice === 'B2Bprice');

  const deliveryTypes = useMemo(
    () => getDeliveryTypesDependOnContract(deliveryType, contractType, solvedPrice),
    [deliveryType, contractType, solvedPrice]
  );

  /** Шуурхай хүргэлтэд газрын зургаар л хаяг авна */
  const canWriteAddress = selectedDeliveryTime !== 'express';

  /** Серверт явуулах байрлал — сонгосон хаяг/PP Box-ийн координат */
  const position = useMemo(() => {
    const { lat, lon } = selectedDeliveryAddress?.location ?? {};
    return lat == null || lon == null ? DEFAULT_POSITION : [lat, lon];
  }, [selectedDeliveryAddress]);

  /** Бүрэн сонгогдсон хаяг (3 үе) — аль табаас сонгосон нь хамаарахгүй */
  const resolvedAddress = useMemo(() => {
    if (isErlian) return normalizeAddressUnits(UB);

    const fromBox = isCabinet ? normalizeAddressUnits(selectedBox?.address, bagHorooId) : [];
    if (isValidAddressUnits(fromBox)) return fromBox;

    return normalizeAddressUnits(addressArray, bagHorooId);
  }, [isErlian, isCabinet, selectedBox, addressArray, bagHorooId]);

  /**
   * Хүргэлтийн бүсийн шалгалт. PP Box, Эрлиан, өөрөө авах тохиолдолд хаяг тогтмол/агуулах
   * тул шалгах шаардлагагүй.
   */
  const isZoneCheckable =
    !selfPickUp && !isCabinet && !isErlian && isValidAddressUnits(resolvedAddress);

  const zoneQuery = useMemo(() => {
    if (!isZoneCheckable) return null;

    return buildZoneQuery({
      selectedTab,
      selectedDeliveryAddress,
      deliveryDetail: deliveryDetailValue,
      khorooId: resolvedAddress[2]?.addressId,
    });
  }, [isZoneCheckable, selectedTab, selectedDeliveryAddress, deliveryDetailValue, resolvedAddress]);

  const zoneState = useOutOfZone(zoneQuery);

  /**
   * Бүсээс хэтэрсэн нэмэлт төлбөр — Step3 дээр харуулна.
   * Серверийн `create-delivery-request-payment` үүнийг бодохгүй тул front талдаа тооцно.
   */
  const outOfZoneFee = isZoneCheckable ? calcOutOfZoneFee(zoneState.data) : 0;

  const searchClient = useMemo(
    () =>
      debounce((phone) => {
        const seq = ++phoneSearchSeq.current;
        const isLatest = () => seq === phoneSearchSeq.current;

        axios
          .post('/api/v1/merchant-customer/search', { phoneNumber: phone })
          .then(({ data }) => {
            if (!isLatest()) return;
            /** Сервер хуудаслалттай ({ docs: [] }) ба массив хэлбэрээр хоёуланг нь буцаадаг */
            const docs = Array.isArray(data.result) ? data.result : (data.result?.docs ?? []);

            if (data.statusCode === 200 && docs.length > 0) {
              setPhoneOptions(
                docs.map((item) => ({
                  ...item,
                  label: item.phoneNumber,
                  value: item.phoneNumber,
                  customer: item,
                }))
              );
            } else {
              setPhoneOptions([]);
            }
          })
          // Блок бие — сумны буцах утга болбол terser нь production-д хасаж чадахгүй
          .catch((e) => {
            console.error('searchClient error: ', e);
          });
      }, 400),
    []
  );

  /**
   * Бөглөж байгаа утгыг контекст рүү (тэндээсээ sessionStorage) хадгална —
   * refresh хийхэд нэр, утас, дэлгэрэнгүй хаяг зэрэг нь сэргэнэ.
   */
  const persistValues = useMemo(
    () => debounce((values) => setFormValuesStep2(values), PERSIST_DELAY),
    [setFormValuesStep2]
  );

  // Алхам солиход хамгийн сүүлийн бичсэн утга нь хадгалагдалгүй үлдэхгүй
  useEffect(() => () => persistValues.flush(), [persistValues]);

  useEffect(() => {
    if (isDeliveryTimeOnlyRegular || selfPickUp) {
      form.setFieldValue('deliveryTime', 'regular');
      setSelelctedDeliveryTime('regular');
    }
    if (isDeliveryTypeOnlyDelivery) {
      form.setFieldValue('deliveryType', 'delivery');
      setSelelctedDeliveryType('delivery');
      setSelelctedDeliveryTime('regular');
    }
    // Сонгосон утгыг формын анхны утга болгож сэргээнэ
    if (
      selectedDeliveryType &&
      deliveryTypes?.find((type) => type.value === selectedDeliveryType)
    ) {
      form.setFieldValue('deliveryType', selectedDeliveryType);
    }
    if (selectedDeliveryTime) {
      form.setFieldValue('deliveryTime', selectedDeliveryTime);
    }
    //eslint-disable-next-line
  }, [isDeliveryTimeOnlyRegular, isDeliveryTypeOnlyDelivery, selfPickUp, form]);

  useEffect(() => {
    if (solvedPrice === 'B2Cprice') setSelectedCustomerField('');
    //eslint-disable-next-line
  }, []);

  /**
   * Шуурхай хүргэлтэд "Гараас бичих" таб байхгүй. Хэрэглэгч тэр табыг нээчихээд
   * шуурхай болговол таб нь харагдахаа болих ч `selectedTab` нь хуучраад
   * хаягийн шалгалт өнгөрдөггүй байсан.
   */
  useEffect(() => {
    if (!canWriteAddress && selectedTab !== 'addressMap') setSelectedTab('addressMap');
    //eslint-disable-next-line
  }, [canWriteAddress, selectedTab]);

  /**
   * Харилцагчийн жагсаалт нь `onSearch`-аар татагддаг тул хуудас сэргэхэд
   * (буцаж ирэх/refresh) хоосон үлдэж, `onFinish` дээр `customerId` олдохгүй
   * байсан — эхний удаа хадгалагдсан дугаараар хайлт хийнэ.
   */
  useEffect(() => {
    // Сэргээгдсэн утгыг blur дээр харилцагчийн мэдээллээр дахин дарж бичихгүй
    prefilledPhoneRef.current = form.getFieldValue('phoneNumber') || null;
    searchClient(form.getFieldValue('phoneNumber') || '');
    //eslint-disable-next-line
  }, []);

  /**
   * Step1 дээр сонгосон харилцагчийн мэдээллийг нэг л удаа шингээнэ.
   * Өмнө нь таб солих бүрд дуудагддаг тул газрын зураг дээр сонгосон хаягийг
   * харилцагчийн хаягаар дарж бичдэг байсан.
   */
  useEffect(() => {
    if (!selectedCustomerField) return;
    // Формд аль хэдийн тэр харилцагчийн мэдээлэл байвал (refresh) дарж бичихгүй
    if (form.getFieldValue('phoneNumber') === selectedCustomerField.phoneNumber) return;

    prefillFromCustomer(selectedCustomerField);
    //eslint-disable-next-line
  }, [selectedCustomerField]);

  useEffect(() => {
    if (!isErlian) return;
    form.setFieldsValue({
      ...ERLIAN_CUSTOMER,
      deliveryDetail: 'PickPack агуулах',
      isErlian,
      deliveryType: 'Гарт хүргэлт энгийн',
    });
    //eslint-disable-next-line
  }, []);

  const onChangeDeliveryType = (type) => {
    setSelelctedDeliveryType(type);
    // Өмнөх төрлийн сонголтууд үлдэхгүй
    setSelectedBox(null);
    setSelectedLocker({});
    form.setFieldsValue({
      dropoffDate: null,
      dropoffTime: null,
      deliveryPositionCode: '',
      pricingCode: null,
      /**
       * "Дэлгэрэнгүй хаяг" талбар нь төрөл сонгосны ДАРАА л гарч ирдэг тул
       * өмнө нь энд үргэлж хоосолдог байсан — харилцагчаас бөглөгдсөн хаяг
       * хэзээ ч харагддаггүй байв. Гарт хүргэлтэд бичсэн/харилцагчийн хаягийг үлдээнэ.
       */
      deliveryDetail:
        type === 'delivery'
          ? form.getFieldValue('deliveryDetail') || getCustomerDeliveryDetail(findCurrentCustomer())
          : '',
    });
  };

  const onChangeDeliveryTime = (type) => setSelelctedDeliveryTime(type);

  /** Нэмэлт талбарын (hub/cabinet) кодоор хаягийн нэрийг олно */
  const getAddressName = (deliveryPositionCode) => {
    if (!deliveryPositionCode) return '';

    const field = customFields.find((item) => item.enum?.includes(deliveryPositionCode));
    return field ? field.enumDictionary[`${deliveryPositionCode}`]?.addressName : '';
  };

  /** Формд байгаа дугаарын харилцагч (Step1-ээс сонгосон эсвэл хайлтаар олдсон) */
  const findCurrentCustomer = () => {
    if (selectedCustomerField) return selectedCustomerField;

    const phone = form.getFieldValue('phoneNumber');
    return phone ? phoneOptions.find((item) => item.phoneNumber === phone) : undefined;
  };

  /** Харилцагчийн хадгалсан дэлгэрэнгүй хаяг, байхгүй бол хот/дүүрэг/хороо */
  const getCustomerDeliveryDetail = (customer) => {
    if (!customer) return '';
    if (customer.addressDetail) return customer.addressDetail;

    return (customer.address ?? [])
      .map((item) => item?.name ?? '')
      .join(' ')
      .trim();
  };

  /** Харилцагчийн мэдээллийг формд болон хаягт буулгана */
  const prefillFromCustomer = (customer) => {
    if (!customer) return;
    prefilledPhoneRef.current = customer?.phoneNumber ?? null;

    const units = normalizeAddressUnits(customer?.address);
    setAddressArray(isValidAddressUnits(units) ? units : (customer?.address ?? []));

    const [hot = '', duureg = '', horoo = ''] = (customer?.address ?? []).map(
      (item) => item?.name ?? ''
    );

    form.setFieldsValue({
      firstName: customer?.name ?? '',
      email: customer?.email ?? '',
      phoneNumber: customer?.phoneNumber ?? '',
      aimagHot: hot,
      sumDuureg: duureg,
      bagHoroo: horoo,
      deliveryDetail: getCustomerDeliveryDetail(customer),
    });
  };

  /** Утасны дугаараар олдсон харилцагчийг сонгох */
  const onSelectPhoneNumber = (value) => {
    prefillFromCustomer(
      selectedCustomerField || phoneOptions.find((item) => item.phoneNumber === value)
    );
  };

  /**
   * Бүртгэлтэй харилцагчийн дугаарыг жагсаалтаас сонголгүй бүтнээр нь бичээд
   * гарсан ч сонгосонтой адил мэдээллийг нь бөглөнө. Нэг харилцагчийг дахин
   * бөглөхгүй — хэрэглэгчийн засварласан нэр, хаяг дарагдахгүй.
   */
  const onBlurPhoneNumber = () => {
    const phone = form.getFieldValue('phoneNumber');
    if (!phone || phone === prefilledPhoneRef.current) return;

    prefillFromCustomer(phoneOptions.find((item) => item.phoneNumber === phone));
  };

  /**
   * Серверээс олдсон харилцагчид — санал болгох жагсаалт. Талбар нь энгийн
   * input тул шинэ дугаарыг сонгох шаардлагагүй, бичсэн утга шууд хадгалагдана.
   */
  const phoneSelectOptions = useMemo(
    () =>
      phoneOptions.map((phone) => {
        const address = formatAddressUnits(phone?.address);

        return {
          value: phone?.phoneNumber,
          label: (
            <div>
              <div>{phone?.name || '-'}</div>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {[phone?.phoneNumber, address].filter(Boolean).join(' · ')}
              </Text>
            </div>
          ),
        };
      }),
    [phoneOptions]
  );

  /** "Гараас бичих" таб дээр хаяг солих */
  const onChangeAddress = (nextAddressArray) => {
    setSelectedBox(null);
    form.setFieldValue('deliveryDetail', '');
    // Газрын зургаас ирсэн хаягийн нэр хоцрохгүй
    setAddressDetail('');

    const normalized = normalizeAddressUnits(nextAddressArray, bagHorooId);
    setAddressArray(isValidAddressUnits(normalized) ? normalized : nextAddressArray);
  };

  const onFinish = (values) => {
    if (Array.isArray(values.dropoffTime) && values.dropoffTime.length === 0) {
      delete values.dropoffTime;
    }

    const data = { ...values, sourceAddress: getAddressName(values.deliveryPositionCode) };

    if (isErlian) {
      data.deliveryType = 'delivery';
      data.isErlian = isErlian;
      Object.assign(data, ERLIAN_CUSTOMER);
    }

    if (isCabinet) {
      if (!selectedBox) {
        message.error('PPBox сонгоогүй байна!');
        return;
      }
      data.deliveryDetail = `${selectedBox.code}, ${selectedBox.addressName}`;
      data.deliveryPositionCode = selectedBox.code;
    }

    /**
     * Хаягийг аль табаас сонгосноос үл хамааран нэг эх сурвалжаас авна:
     * газрын зураг ба "Гараас бичих" хоёул `addressArray`-д бичдэг тул
     * ХАМГИЙН СҮҮЛД сонгосон хаяг хэрэглэгдэнэ (нэгийг нь дуугүй дарж бичихгүй).
     */
    const address = selfPickUp ? [] : resolvedAddress;

    if (!selfPickUp && !isValidAddressUnits(address)) {
      message.error(
        selectedTab === 'addressMap'
          ? 'Газрын зураг дээрээс хүлээж авах хаягаа сонгоно уу!'
          : 'Хот/аймаг, дүүрэг/сум, хороо/багаа бүрэн сонгоно уу!'
      );
      return;
    }

    if (address.length) setAddressArray(address);

    data.addressArray = address;
    data.position = position;

    if (values?.phoneNumber && phoneOptions?.length) {
      const customer = phoneOptions?.find((item) => item?.value === values?.phoneNumber);

      let customerUpdate = false;
      if (values.firstName !== customer?.name) {
        customerUpdate = true;
      } else if (values.email !== customer?.email) {
        customerUpdate = true;
      } else if (!values.deliveryDetail) {
        for (let i = 0; i < customer?.address?.length; i++) {
          if (address?.[i]?.addressId !== customer?.address[i]?.addressId) {
            customerUpdate = true;
            break;
          }
        }
      }

      data.customerId = customer?._id;
      data.customerUpdate = customerUpdate;
    }

    const outOfZoneDistanceKm = outOfZoneFee ? zoneState.data?.distanceKm : null;

    setSubmitting(true);
    axios
      .post('/api/v1/request/create-delivery-request-payment', {
        warehouseList,
        branchList,
        solvedPrice: formDataStep1?.solvedPrice,
        address,
        selfPickUp,
        position,
        deliveryType: selectedDeliveryType,
        type: selectedDeliveryTime,
        warehouseCode: formDataStep1?.warehouseCode,
        contractType: formDataStep1?.contractType,
        outOfZoneDistanceKm,
      })
      .then(({ data: res }) => {
        if (res.statusCode !== 200) {
          // Interceptor мэдэгдээгүй кодод л локал мэдэгдэл гаргана — эс тэгвээс давхардана
          if (!isHandledByInterceptor(res)) {
            message.error('Захиалга үнэ бодоход алдаа гарлаа. Мэдээлэл дахин шалгана уу!');
          }
          return;
        }
        /**
         * Зөвхөн үнэ бодолт БҮТСЭН үед л Step2-ийн мэдээллийг батална —
         * `formDataStep2` байгаа эсэхээр 3-р алхам руу орох эрхийг шалгадаг
         * (Add.js) тул алдаа гарсан үед 3-р алхам руу орох боломжгүй байна.
         */
        setFormDataStep2({
          ...data,
          deliveryPrice: res?.result?.deliveryAmount?.totalPrice,
          executionPrice: res?.result?.executionAmount?.totalPrice,
          totalPrice: res?.result?.totalAmount || 0,
          expressAmount: res?.result?.expressAmount?.totalPrice || 0,
          exceededAmount: res?.result?.expressAmount?.eachLengthKmPrice || 0,
          outOfZoneFee,
          outOfZoneDistanceKm,
        });
        nextStep();
      })
      .catch((e) => {
        // Мэдэгдлийг axios-ийн global interceptor гаргана — энд давхардуулахгүй
        console.error('create-delivery-request-payment error: ', e);
      })
      .finally(() => setSubmitting(false));
  };

  const addressTabs = [
    {
      key: 'addressMap',
      label: 'Газрын зураг',
      children: isCabinet ? (
        <CabinetMap
          selectedLocker={selectedLocker}
          setSelectedLocker={setSelectedLocker}
          selectedBox={selectedBox}
          setSelectedBox={setSelectedBox}
        />
      ) : (
        <AddressMap />
      ),
    },
  ];

  if (canWriteAddress) {
    addressTabs.push({
      key: 'addressDetail',
      label: 'Гараас бичих',
      children: (
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
          <div style={{ maxWidth: FORM_MAX_WIDTH }}>
            {selectedTab === 'addressDetail' && (
              <AddressUnitsSelector
                selectedUnits={addressArray}
                onChange={onChangeAddress}
                form={form}
                isErlian={isErlian}
                styleProps={addressStyleProps}
                selectedCustomerField={selectedCustomerField}
              />
            )}
          </div>

          {customFields.map((field) => getCustomField(field))}

          {isCabinet && addressArray?.[2]?.addressId && (
            <PPBoxSelection
              committeeId={addressArray[2].addressId}
              selectedBox={selectedBox}
              setSelectedBox={setSelectedBox}
            />
          )}
        </Space>
      ),
    });
  }

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={onFinish}
      initialValues={{ ...formDataStep2, ...formValuesStep2 }}
      onValuesChange={(_, allValues) => persistValues(allValues)}
    >
      <Space direction="vertical" size={16} style={{ width: '100%' }}>
        <SectionCard icon={<AiOutlineInfoCircle />} title="Харилцагчийн мэдээлэл">
          <Row gutter={16} style={{ maxWidth: FORM_MAX_WIDTH }}>
            <Col xs={24} sm={12} xl={8}>
              <Form.Item
                label="Нэр"
                name="firstName"
                // `required` нь зөвхөн '' -ыг хоосонд тооцдог тул зөвхөн зайгаар
                // бөглөсөн нэр валидацаас нэвтэрч, нэргүй захиалга үүсдэг байв.
                // Нэр нь харилцагч болж хадгалагдах тул Харилцагч нэмэхтэй ижил хязгаартай
                rules={[
                  requiredText('Нэрээ оруулна уу!'),
                  maxLengthRule('Нэр', CUSTOMER_NAME_MAX_LENGTH),
                ]}
              >
                <Input
                  placeholder="Нэр"
                  maxLength={CUSTOMER_NAME_MAX_LENGTH}
                  disabled={Boolean(selectedCustomerField)}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} xl={8}>
              <Form.Item
                label="Утасны дугаар"
                name="phoneNumber"
                // Өмнө нь зөвхөн `required` байсан тул тоо биш, эсвэл 8 оронгүй
                // утга дээр ямар ч мессеж гарахгүй, зүгээр хоосон жагсаалт
                // үлддэг байв — Тохиргоо > Ажилтантай ижил дүрэм хэрэглэв
                rules={phoneNumberRules('Утасны дугаараа оруулна уу!')}
              >
                {/*
                 * Өмнө нь Select байсан тул шинэ дугаарыг бичээд заавал
                 * сонгохгүй бол утга нь хадгалагдахгүй алга болдог байв —
                 * AutoComplete нь энгийн input, бүртгэлтэй харилцагчийг л
                 * санал болгоно.
                 */}
                <AutoComplete
                  onSelect={onSelectPhoneNumber}
                  onBlur={onBlurPhoneNumber}
                  onSearch={searchClient}
                  /* Хайлт нь серверт хийгддэг тул локалаар дахин шүүхгүй */
                  filterOption={false}
                  onFocus={() => searchClient(phoneValue || '')}
                  disabled={Boolean(selectedCustomerField)}
                  options={phoneSelectOptions}
                >
                  <Input placeholder="Утас" inputMode="numeric" maxLength={8} />
                </AutoComplete>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} xl={8}>
              <Form.Item
                label="Имэйл хаяг"
                name="email"
                rules={[
                  { type: 'email', message: 'Имэйл хаяг буруу байна.' },
                  maxLengthRule('Имэйл', EMAIL_MAX_LENGTH),
                ]}
              >
                <Input
                  placeholder="И-мэйл"
                  type="email"
                  maxLength={EMAIL_MAX_LENGTH}
                  disabled={Boolean(selectedCustomerField)}
                />
              </Form.Item>
            </Col>
            {merchantCode === 'EOV' && (
              <Col xs={24} sm={12} xl={8}>
                <Form.Item
                  label="Агуулахын код"
                  name="ingtegration_Warehouse"
                  rules={[{ required: true, message: 'Агуулахын код оруулна уу!' }]}
                >
                  <Input placeholder="Агуулахын код" />
                </Form.Item>
              </Col>
            )}
          </Row>
        </SectionCard>

        {(!isDeliveryTypeOnlyDelivery || (!isDeliveryTimeOnlyRegular && !selfPickUp)) && (
          <SectionCard icon={<MdDateRange />} title="Хүргэлтийн төрөл, хугацаа">
            <Row gutter={16} style={{ maxWidth: FORM_MAX_WIDTH }}>
              {!isDeliveryTypeOnlyDelivery && (
                <Col xs={24} sm={12} xl={8}>
                  <Form.Item
                    label="Хүргэлтийн төрөл"
                    name="deliveryType"
                    rules={[{ required: true, message: 'Хүргэлтийн төрлөө сонгоно уу.' }]}
                    style={{ marginBottom: 0 }}
                  >
                    <Select onChange={onChangeDeliveryType} placeholder="Сонгох">
                      {deliveryTypes?.map(({ type, value }) => (
                        <Option key={value} value={value} label={type}>
                          {type}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
              )}

              {!isDeliveryTimeOnlyRegular && !selfPickUp && (
                <>
                  <Col xs={24} sm={12} xl={8}>
                    <Form.Item
                      label="Хүргэлтийн хугацаа"
                      name="deliveryTime"
                      rules={[{ required: true, message: 'Хүргэлтийн хугацаа сонгоно уу.' }]}
                      style={{ marginBottom: 0 }}
                    >
                      <Select onChange={onChangeDeliveryTime} placeholder="Сонгох">
                        {deliveryTime?.type?.map((type) => (
                          <Option key={type} value={type} label={deliveryTime.detail[type]}>
                            {deliveryTime.detail[type]}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                    {selectedDeliveryTime === 'express' && (
                      <Space size={8} align="start" style={{ marginTop: 8 }}>
                        <InfoCircleOutlined style={{ color: '#FAA53D' }} />
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          Таны захиалга үүссэнээс хойш 120 минутын дотор хүргэгдэнэ!
                        </Text>
                      </Space>
                    )}
                  </Col>

                  {reference?.isExpress && selectedDeliveryTime === 'regular' && (
                    <Col xs={24} sm={12} xl={8}>
                      <Form.Item
                        label="Хүргэлтийн огноо"
                        name="dropoffDate"
                        rules={[{ required: true, message: 'Хүргэлтийн огноо сонгоно уу.' }]}
                        style={{ marginBottom: 0 }}
                      >
                        <DatePicker style={{ width: '100%' }} placeholder="Огноо сонгох" />
                      </Form.Item>
                    </Col>
                  )}
                </>
              )}
            </Row>
          </SectionCard>
        )}

        {!selfPickUp && selectedDeliveryType && (
          <SectionCard
            icon={<CiLocationOn />}
            title="Хүлээж авах хаяг"
            /* Бүсээс хэтэрсэн км — зөвхөн мэдээлэл, захиалга үүсгэхийг зогсоохгүй */
            action={
              isZoneCheckable && (
                <OutOfZoneNotice loading={zoneState.loading} data={zoneState.data} />
              )
            }
            styles={{ body: { paddingTop: 0 } }}
          >
            <Tabs
              activeKey={selectedTab}
              onChange={(activeKey) => setSelectedTab(activeKey)}
              items={addressTabs}
            />

            <Space direction="vertical" size={16} style={{ width: '100%' }}>
              {/* Аль табаас сонгосон нь хамаарахгүй — хэрэглэгдэх хаягийг харуулна */}
              <Text type={isValidAddressUnits(resolvedAddress) ? 'secondary' : 'danger'}>
                Сонгосон хаяг:{' '}
                <Text strong>
                  {isValidAddressUnits(resolvedAddress)
                    ? formatAddressUnits(resolvedAddress)
                    : 'сонгогдоогүй'}
                </Text>
              </Text>

              {/* Дэлгэрэнгүй хаяг нь хоёр табд ижил тул нэг л удаа гарна */}
              {selectedDeliveryType === 'delivery' && (
                <Row gutter={16} style={{ maxWidth: FORM_MAX_WIDTH }}>
                  <Col xs={24} md={16} xl={12}>
                    <Form.Item
                      label="Дэлгэрэнгүй хаяг"
                      name="deliveryDetail"
                      rules={[{ required: true, message: 'Дэлгэрэнгүй хаягаа оруулна уу!' }]}
                      style={{ marginBottom: 0 }}
                    >
                      <Input placeholder="Давхар, тоот гэх мэт" />
                    </Form.Item>
                  </Col>
                </Row>
              )}
            </Space>
          </SectionCard>
        )}

        {(selectedDeliveryType === 'delivery' || isCabinet) && (
          <SectionCard icon={<IoDocumentTextOutline />} title="Нэмэлт">
            <Space
              direction="vertical"
              size={16}
              style={{ width: '100%', maxWidth: FORM_MAX_WIDTH }}
            >
              <Form.Item
                label="Анхаарах шаардлагатай зүйлс"
                name="notes"
                style={{ marginBottom: 0 }}
              >
                <Select
                  mode="multiple"
                  placeholder="Анхаарах шаардлагатай зүйлс"
                  optionLabelProp="label"
                  style={{ width: '100%' }}
                >
                  {reference?.importantNotes?.map((value) => (
                    <Option key={value} value={value} label={value}>
                      {value}
                    </Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item
                name="orderDescription"
                label="Нэмэлт тэмдэглэл"
                style={{ marginBottom: 0 }}
              >
                <TextArea rows={4} placeholder="Энд бичих" />
              </Form.Item>
            </Space>
          </SectionCard>
        )}

        <StepFooter onPrev={prevStep} loading={submitting} />
      </Space>
    </Form>
  );
};

export default Step2;
